import { createAsyncThunk } from "@reduxjs/toolkit";
import { AppThunkAPIType } from "../../app/appTypes";
import { getWithAuth, postWithAuth, putWithAuth } from "../../http";

export type AiAssistantMessageRole =
    | "USER"
    | "ASSISTANT";

export type AiAssistantMessageContentType =
    | "TEXT"
    | "URS_REQUIREMENT_PROPOSALS";

export interface UrsRequirementProposal {
    content: string;
}

export interface UrsRequirementProposalData {
    requirements: UrsRequirementProposal[];
}

export interface AiAssistantMessage {
    id: number | string;

    role: AiAssistantMessageRole;

    contentType: AiAssistantMessageContentType;

    content: string;

    data?: unknown;
}

export interface AiAssistantSessionDto {
    id: number;
    contextId?: number;
    messages: AiAssistantMessage[];
}

export interface AiAssistantSendMessageResponse {
    message: AiAssistantMessage;
}

export interface AiAssistantSessionRequest {
    contextId: number;
    assistantCode: string;
    initialInstruction: string;
}

export interface AiAssistantSendMessageRequest {
    sessionId: number;
    message: string;
}

export interface AiAssistantConfigurationDto {
    id: string;
    sourceConfigurationId: string | null;
    assistantCode: string;
    model: string;
    instructions: string;
    isSystem: boolean;
}

export interface UpsertCompanyAiAssistantConfigurationDto {
    model: string;
    instructions: string;
}

export const getAiAssistantConfiguration = createAsyncThunk<
    AiAssistantConfigurationDto,
    string,
    AppThunkAPIType
>(
    "aiAssistant/getConfiguration",
    async (configurationId, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            getWithAuth({
                url: `api/ai-assistant-configurations/${configurationId}`
            })
        );

        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as AiAssistantConfigurationDto;
        }

        return rejectWithValue(
            payload ?? { kind: "unknown" }
        );
    }
);

export const upsertCompanyAiAssistantConfiguration = createAsyncThunk<
    AiAssistantConfigurationDto,
    { configurationId: string; dto: UpsertCompanyAiAssistantConfigurationDto },
    AppThunkAPIType
>(
    "aiAssistant/upsertCompanyConfiguration",
    async ({ configurationId, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            putWithAuth({
                url: `api/ai-assistant-configurations/${configurationId}`,
                payload: dto,
            })
        );

        const { payload } = result;

        if (putWithAuth.fulfilled.match(result)) {
            return payload as AiAssistantConfigurationDto;
        }

        return rejectWithValue(
            payload ?? { kind: "unknown" }
        );
    }
);

export const loadOrCreateAiAssistantSession = createAsyncThunk<
    AiAssistantSessionDto,
    AiAssistantSessionRequest,
    AppThunkAPIType
>(
    "aiAssistant/loadOrCreateSession",
    async (
        request,
        { dispatch, rejectWithValue }
    ) => {
        const result = await dispatch(
            postWithAuth({
                url: "api/ai-assistant/sessions/get-or-create",
                payload: request
            })
        );

        const { payload } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return payload as AiAssistantSessionDto;
        }

        return rejectWithValue(
            payload ?? { kind: "unknown" }
        );
    }
);

export const createAiAssistantSession = createAsyncThunk<
    AiAssistantSessionDto,
    AiAssistantSessionRequest,
    AppThunkAPIType
>(
    "aiAssistant/createSession",
    async (
        request,
        { dispatch, rejectWithValue }
    ) => {
        const result = await dispatch(
            postWithAuth({
                url: "api/ai-assistant/sessions",
                payload: request
            })
        );

        const { payload } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return payload as AiAssistantSessionDto;
        }

        return rejectWithValue(
            payload ?? { kind: "unknown" }
        );
    }
);

export const sendAiAssistantMessage = createAsyncThunk<
    AiAssistantSendMessageResponse,
    AiAssistantSendMessageRequest,
    AppThunkAPIType
>(
    "aiAssistant/sendMessage",
    async (
        { sessionId, message },
        { dispatch, rejectWithValue }
    ) => {
        const result = await dispatch(
            postWithAuth({
                url: `api/ai-assistant/sessions/${sessionId}/messages`,
                payload: { message }
            })
        );

        const { payload } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return payload as AiAssistantSendMessageResponse;
        }

        return rejectWithValue(
            payload ?? { kind: "unknown" }
        );
    }
);